/**
 * Coordinate handling for document markup (BBD-125).
 *
 * Strokes are stored in page space: PDF units at scale 1 for PDFs, natural
 * image pixels for PNG/JPG markups. The origin is the top-left corner of the
 * page as it is displayed, so a stroke lands in the same place whatever size
 * the page is rendered at. Such strokes carry `coord_space: 'pdf'`.
 *
 * Strokes saved before the fix have no `coord_space`. They hold on-screen
 * pixels at a zoom level that was never stored, so they can only be converted
 * by estimate (see legacyWorkspaceSize).
 */

export const MARKUP_COORD_SPACE = 'pdf';

// The page is fitted into the workspace, but never enlarged past 2x.
const MAX_FIT_SCALE = 2;

// Header, toolbar and footer of the markup editor, which the page never covers.
const EDITOR_CHROME_HEIGHT = 177;
// The workspace of a maximised browser window on a 1920x1080 display.
const DEFAULT_DESKTOP_WORKSPACE = { width: 1920, height: 776 };
const DESKTOP_MIN_WIDTH = 1024;

const round2 = (value) => Math.round(value * 100) / 100;

export const fitScale = (pageSize, workspace) => {
    const scale = Math.min(
        workspace.width / pageSize.width,
        workspace.height / pageSize.height,
        MAX_FIT_SCALE
    );
    return Number.isFinite(scale) && scale > 0 ? scale : 1;
};

export const toPagePoint = (point, scale) => ({
    ...point,
    x: round2(point.x / scale),
    y: round2(point.y / scale),
});

export const toPageLength = (length, scale) => round2(length / scale);

export const isLegacyStroke = (stroke) => Boolean(stroke) && stroke.coord_space !== MARKUP_COORD_SPACE;

/**
 * The workspace size to assume for legacy strokes.
 *
 * Annotating needs a mouse, so legacy strokes were drawn in a desktop window.
 * On a desktop the best guess is the window that is open now: someone who
 * reopens the document on the same machine keeps their annotations exactly
 * where they see them today. `measured` is the editor's real workspace when it
 * is on screen; without it the window size minus the editor chrome is used.
 * On a phone or tablet the current window says nothing about the original
 * one, so a default desktop window is assumed.
 */
export const legacyWorkspaceSize = (measured = null) => {
    if (typeof window === 'undefined' || window.innerWidth < DESKTOP_MIN_WIDTH) {
        return DEFAULT_DESKTOP_WORKSPACE;
    }
    if (measured && measured.width > 0 && measured.height > 0) {
        return { width: measured.width, height: measured.height };
    }
    return {
        width: window.innerWidth,
        height: Math.max(window.innerHeight - EDITOR_CHROME_HEIGHT, 1),
    };
};

const convertLegacyStroke = (stroke, scale) => ({
    ...stroke,
    coord_space: MARKUP_COORD_SPACE,
    style: {
        ...stroke.style,
        thickness: toPageLength(stroke.style?.thickness || 2, scale),
    },
    points: (stroke.points || []).map((point) => toPagePoint(point, scale)),
});

/**
 * Convert the legacy strokes of one page into page space. Strokes that are
 * already in page space are returned untouched.
 */
export const convertLegacyStrokes = (strokes, pageSize, workspace) => {
    const scale = fitScale(pageSize, workspace);
    let converted = 0;
    const result = (strokes || []).map((stroke) => {
        if (!isLegacyStroke(stroke)) return stroke;
        converted += 1;
        return convertLegacyStroke(stroke, scale);
    });
    return { strokes: result, converted };
};
